import {
  Box,
  Button,
  Collapsible,
  FieldWrapper,
  FlexBox,
  Icon,
  InputSelectionList,
  InputSelectionListChangeEvent,
  Text,
} from '@dixa/lib-shared-habla';
import type { SearchFilterOperator } from 'lib-search-types';
import { SearchFilterLayout } from 'lib-search-ui';
import { memo, useCallback, useEffect, useState } from 'react';

import styles from './contactsFilterOptions.module.scss';

type ContactsFilterOptionsProps<TOperator extends SearchFilterOperator> = {
  operators: readonly {
    value: TOperator;
    label: string;
  }[];
  defaultOperator?: TOperator;
  selectedOperator?: TOperator;
  isMissingSelectedValues?: boolean;
  isDropdownOpen?: boolean;
  isDefaultOpen?: boolean;
  onChangeOperator: (operator: TOperator) => void;
  onClear?: VoidFunction;
};

const ContactsFilterOptions = <TOperator extends SearchFilterOperator>({
  operators,
  defaultOperator,
  selectedOperator,
  isMissingSelectedValues = true,
  isDropdownOpen = true,
  isDefaultOpen = false,
  onChangeOperator,
  onClear,
}: ContactsFilterOptionsProps<TOperator>): JSX.Element => {
  const [isOpen, setIsOpen] = useState(isDefaultOpen);

  useEffect(() => {
    if (!isDefaultOpen) {
      setIsOpen(isDropdownOpen && selectedOperator !== defaultOperator);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDefaultOpen, isDropdownOpen]);

  const handleChangeOperator = useCallback(
    (event: InputSelectionListChangeEvent<TOperator>) => {
      if (event.selectedItem && event.selectedItem.value !== selectedOperator) {
        onChangeOperator(event.selectedItem.value);
      }
    },
    [onChangeOperator, selectedOperator],
  );

  const isClearable =
    onClear &&
    (!isMissingSelectedValues ||
      (selectedOperator && selectedOperator !== defaultOperator));

  return (
    <Collapsible
      isOpen={isOpen}
      header={
        <FlexBox
          alignItems="center"
          justifyContent="space-between"
          height="32px"
        >
          <Text.ExternalLink
            className={styles.link}
            as="div"
            onClick={() => setIsOpen(!isOpen)}
          >
            <FlexBox gap="200" alignItems="center">
              <Icon name={isOpen ? 'angle-down' : 'angle-right'} />
              Advanced options
            </FlexBox>
          </Text.ExternalLink>

          {isClearable && (
            <Button variant="secondary" onClick={onClear}>
              Clear
            </Button>
          )}
        </FlexBox>
      }
    >
      <Box padding="300 0 0 0">
        <FieldWrapper label="Match..." noAlert>
          <InputSelectionList<TOperator>
            value={selectedOperator ? [selectedOperator] : undefined}
            placeholder="Select an operator"
            disabled={!isOpen}
            onChange={handleChangeOperator}
            getItemTitleByValue={(op) =>
              operators.find((o) => o.value === op)?.label ?? null
            }
            dropdownProps={{ fixed: true, className: styles.dropdown }}
          >
            {operators.map((op) => (
              <SearchFilterLayout.Item
                key={op.value}
                value={op.value}
                label={op.label}
              />
            ))}
          </InputSelectionList>
        </FieldWrapper>
      </Box>
    </Collapsible>
  );
};

const _ContactsFilterOptions = memo(
  ContactsFilterOptions,
) as typeof ContactsFilterOptions;

export { _ContactsFilterOptions as ContactsFilterOptions };
